'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackViewSacItemList } from '@sola/tracking/components/datalayer.tsx';
import { CardList } from '../card-list/card-list.tsx';
import { FeatureButtons } from '../feature-buttons/feature-buttons.tsx';
import { SacCard } from '../sac-card/sac-card.tsx';
import { SacEmpty } from '../sac-empty/sac-empty.tsx';
import { SacListSkeleton } from './sac-list.skeleton.tsx';
import { useSacChildren } from './useSacChildren.ts';
type SacListLayoutProps = {
  allowShowMore?: boolean;
  columns?: '1' | '2';
  columnsSm?: '1' | '2';
  columnsMd?: '1' | '2' | '3';
  columnsLg?: '1' | '2' | '3';
};
const defaultLimit = 12;
const InternalSacList: React.FC<SacFilterProps & SacListLayoutProps> = t0 => {
  const $ = _c(39);
  const {
    limit: t1,
    gender,
    ageMin,
    ageMax,
    countries,
    allowShowMore: t2,
    columns: t3,
    columnsSm: t4,
    columnsMd: t5,
    columnsLg: t6,
    hiv,
    orphan,
    disability,
    highlyVulnerable
  } = t0;
  const limit = t1 === undefined ? defaultLimit : t1;
  const allowShowMore = t2 === undefined ? false : t2;
  const columns = t3 === undefined ? 1 : t3;
  const columnsSm = t4 === undefined ? 1 : t4;
  const columnsMd = t5 === undefined ? 2 : t5;
  const columnsLg = t6 === undefined ? 3 : t6;
  let t7;
  if ($[0] !== ageMax || $[1] !== ageMin || $[2] !== columns || $[3] !== columnsLg || $[4] !== columnsMd || $[5] !== columnsSm || $[6] !== countries || $[7] !== disability || $[8] !== gender || $[9] !== highlyVulnerable || $[10] !== hiv || $[11] !== orphan) {
    t7 = {
      limit: defaultLimit,
      gender,
      ageMin,
      ageMax,
      countries,
      allowShowMore: false,
      columns,
      columnsSm,
      columnsMd,
      columnsLg,
      hiv,
      orphan,
      disability,
      highlyVulnerable
    };
    $[0] = ageMax;
    $[1] = ageMin;
    $[2] = columns;
    $[3] = columnsLg;
    $[4] = columnsMd;
    $[5] = columnsSm;
    $[6] = countries;
    $[7] = disability;
    $[8] = gender;
    $[9] = highlyVulnerable;
    $[10] = hiv;
    $[11] = orphan;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const filters = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["children"];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    t
  } = useTranslation(t8);
  const {
    isLoading,
    children,
    showRichMix,
    showLoadMoreButton,
    incrementPage,
    resetFilters
  } = useSacChildren(filters, allowShowMore);
  if (isLoading) {
    let t9;
    if ($[14] !== limit) {
      t9 = <SacListSkeleton itemCount={limit} />;
      $[14] = limit;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    return t9;
  }
  if (children.length === 0) {
    let t9;
    if ($[16] !== resetFilters) {
      t9 = <SacEmpty resetFilters={resetFilters} />;
      $[16] = resetFilters;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const displayedChildren = children;
  let t9;
  if ($[18] !== columns || $[19] !== columnsLg || $[20] !== columnsMd || $[21] !== columnsSm || $[22] !== displayedChildren) {
    t9 = displayedChildren && <><CardList columns={Number(columns)} columnsSm={Number(columnsSm)} columnsMd={Number(columnsMd)} columnsLg={Number(columnsLg)} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{displayedChildren.map(_temp)}</CardList><TrackViewSacItemList listId="sac" listName="Sac List" items={displayedChildren} /></>;
    $[18] = columns;
    $[19] = columnsLg;
    $[20] = columnsMd;
    $[21] = columnsSm;
    $[22] = displayedChildren;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== showLoadMoreButton || $[25] !== showRichMix || $[26] !== t) {
    t10 = !showLoadMoreButton && <div className="w-full text-center pt-roomy-xs body-md max-w-sm mx-auto">{showRichMix && t("children:sac.useSearchControls")}{!showRichMix && t("children:sac.useClearFilters")}</div>;
    $[24] = showLoadMoreButton;
    $[25] = showRichMix;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== allowShowMore || $[29] !== incrementPage || $[30] !== resetFilters || $[31] !== showLoadMoreButton || $[32] !== showRichMix || $[33] !== t) {
    t11 = allowShowMore && <FeatureButtons variant="grow" className="w-full justify-center mt-comfy-xl md:mt-roomy-xs">{showLoadMoreButton && <ButtonStyle><button type="button" onClick={() => incrementPage()}>{t("children:sac.viewMoreChildren")}</button></ButtonStyle>}{!showLoadMoreButton && !showRichMix && <ButtonStyle variant="secondary"><button type="button" onClick={resetFilters}>{t("children:sac.clearFilters")}</button></ButtonStyle>}<ButtonStyle variant={!showLoadMoreButton && !showRichMix ? "primary" : "secondary"}><Link href="/sponsor-a-child/find-your-match/" className="w-full md:w-fit" type="button">{t("children:sac.helpFindMatch")}</Link></ButtonStyle></FeatureButtons>;
    $[28] = allowShowMore;
    $[29] = incrementPage;
    $[30] = resetFilters;
    $[31] = showLoadMoreButton;
    $[32] = showRichMix;
    $[33] = t;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export const SacList: React.FC<SacFilterProps & SacListLayoutProps> = props => {
  const $ = _c(7);
  const t0 = props.limit || defaultLimit;
  let t1;
  if ($[0] !== t0) {
    t1 = <SacListSkeleton itemCount={t0} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <InternalSacList {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Suspense fallback={t1}>{t2}</Suspense>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(child) {
  return <SacCard key={child.globalId} {...child} />;
}